'use client';
import {
    MotionMaybe,
    normalizeAnimationPreset,
    useMotionControls,
} from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { useEffect, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

export default function ImageGallery(props) {
    const {
        MainHeader,
        Tagline,
        Images = [],
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        OutlineButtonText,
        SecondaryButtonLink,
        OutlineButtonLink,
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        Animation = 'fade',
        Duration = 0.6,
        AnimationGroup = 'content',
        EditorPreview = false,
    } = props;

    const [displayedImageCount, setDisplayedImageCount] = useState(0);
    const [currentImageSize, setCurrentImageSize] = useState({});

    const {
        backgroundColor,
        headerColor,
        thirdButton,
        secondaryButton,
        taglineColor,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const normalizedGroup = (() => {
        const raw = String(AnimationGroup ?? '')
            .toLowerCase()
            .trim();
        if (
            [
                'image',
                'images',
                'gallery',
                'features',
                'media',
                'medias',
            ].includes(raw)
        )
            return 'features';
        if (['cta', 'ctas', 'button', 'buttons'].includes(raw)) return 'ctas';
        if (raw === 'all') return 'all';
        return 'content';
    })();
    const normalizedPreset = normalizeAnimationPreset(
        Animation,
        normalizedGroup === 'features' ? 'features' : 'content'
    );
    const {
        enableAnim,
        isStagger,
        childMotionProps,
        containerMotionProps,
        enableChild,
        remountKey,
    } = useMotionControls({
        preset: normalizedPreset,
        duration: Number(Duration) || 0.6,
        editorPreview: EditorPreview,
        group: normalizedGroup === 'all' ? 'content' : normalizedGroup,
        keyBase: 'image-gallery',
    });
    const shouldAnimate = (group) =>
        enableAnim && (normalizedGroup === 'all' || enableChild(group));
    const animateContent = shouldAnimate('content');
    const animateImages = shouldAnimate('features');
    const animateCTAs = shouldAnimate('ctas');
    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            wrapSpacing: 'mt-10 lg:mt-16',
            className: 'relative z-40',
            buttonsClassName:
                'flex flex-wrap items-center justify-center gap-4',
            enableAnim: animateCTAs,
            motionProps: childMotionProps,
        }
    );

    useEffect(() => {
        function handleResize() {
            const count = getImageCount();
            setDisplayedImageCount(count);
            const newSize = determineImageSize(count);
            setCurrentImageSize(newSize); // Store the calculated size
        }

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [Images.length]);

    function getImageCount() {
        const width = window.innerWidth;
        let count;

        if (width <= 640) {
            count = Math.min(Images.length, 3);
        } else if (width <= 768) {
            count = Math.min(Images.length, 4);
        } else if (width <= 1280) {
            count = Math.min(Images.length, 6);
            if (count === 5) {
                count = 4; // Reduce count to 4 if it would otherwise be 5
            }
        } else {
            count = Math.min(Images.length, 9);
            if (count === 7) {
                count = 6; // Adjust if count is 7 for larger screens
            }
            if (count === 5) {
                count = 4; // Adjust if count is 7 for larger screens
            }
        }

        return count;
    }

    function determineImageSize(imageCount) {
        const width = window.innerWidth;

        if (width <= 748) {
            return { width: '280px', height: '280px' };
        } else if (width > 749 && width <= 1024) {
            switch (imageCount) {
                case 1:
                    return { width: '500px', height: '500px' };
                case 2:
                case 4:
                    return { width: '300px', height: '300px' };
                case 3:
                case 6:
                    return { width: '200px', height: '200px' };
                case 8:
                    return { width: '110px', height: '110px' }; // Size for 8 images
                case 9:
                    return { width: '100px', height: '100px' }; // Size for 9 images
                default:
                    return { width: '100px', height: '100px' }; // Default size
            }
        } else {
            // Desktop
            switch (imageCount) {
                case 1:
                    return { width: '500px', height: '500px' };
                case 2:
                    return { width: '480px', height: '480px' };
                case 4:
                    return { width: '220px', height: '220px' };
                case 3:
                case 6:
                    return { width: '300px', height: '300px' };
                case 8:
                    return { width: '290px', height: '290px' }; // Size for 8 images
                case 9:
                    return { width: '390px', height: '390px' }; // Size for 9 images
                default:
                    return { width: '100px', height: '100px' }; // Default size
            }
        }
    }

    let numColumns;

    if (Images.length === 1) {
        numColumns = 'grid-cols-1';
    } else if (Images.length === 2) {
        numColumns = 'grid-cols-1 md:grid-cols-2';
    } else if (Images.length === 3) {
        numColumns = 'grid-cols-1 md:grid-cols-3';
    } else if (Images.length === 4) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
    } else if (Images.length === 5) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
    } else if (Images.length === 6) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
    } else if (Images.length === 7) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
    } else if (Images.length === 8) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4';
    } else {
        // For 9 or more images
        numColumns = 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
    }

    return (
        <div data-json="imagegallery">
            <div
                className={`editable-component relative ${backgroundColor} overflow-hidden px-6 lg:px-8 ${vSpace}`}
                style={backgroundStyle}
            >
                {MainHeader && (
                    <MotionMaybe
                        enable={animateContent}
                        nodeKey={`${remountKey}-header`}
                        motionProps={childMotionProps}
                        className="relative z-40 mx-auto px-6 pb-16 sm:max-w-xl md:max-w-full md:px-24 lg:max-w-screen-xl lg:px-8"
                    >
                        <div className="mb-6 max-w-xl text-center md:mx-auto md:mb-10 lg:max-w-2xl">
                            <div>
                                <MarkDown
                                    className={`font-display text-base uppercase leading-7 ${taglineColor} tracking-wide text-primary-900`}
                                >
                                    {Tagline}
                                </MarkDown>
                            </div>

                            <MarkDown
                                variant="h2"
                                className={`mt-2 font-display text-3xl tracking-tight ${headerColor} sm:text-4xl sm:leading-tight`}
                            >
                                {MainHeader}
                            </MarkDown>
                        </div>
                    </MotionMaybe>
                )}

                <div className="relative z-40 mx-auto flex max-w-full justify-center">
                    <MotionMaybe
                        enable={animateImages && isStagger}
                        nodeKey={`${remountKey}-grid`}
                        motionProps={containerMotionProps}
                        className={`grid ${numColumns} gap-8 sm:gap-y-8 lg:items-start ${
                            Images.length === 1 ? 'lg:justify-center' : ''
                        }`}
                    >
                        {Images.slice(0, displayedImageCount).map(
                            (Showcase, index) => {
                                return (
                                    <MotionMaybe
                                        key={index}
                                        enable={animateImages}
                                        nodeKey={`${remountKey}-img-${index}`}
                                        motionProps={childMotionProps}
                                    >
                                        <Image
                                            {...Showcase}
                                            style={currentImageSize}
                                        />
                                    </MotionMaybe>
                                );
                            }
                        )}
                    </MotionMaybe>
                </div>

                {/* CTA */}
                {hasCTAs && (
                    <MotionMaybe
                        enable={animateCTAs}
                        nodeKey={`${remountKey}-ctas`}
                        motionProps={childMotionProps}
                    >
                        {CTAs}
                    </MotionMaybe>
                )}
            </div>
        </div>
    );
}

function Image(props) {
    const { ImageUrl, AltText, ImagePosition = '50', style } = props;
    const { x: imagePosX, y: imagePosY } = parseImagePosition(ImagePosition);
    const imageStyle = {
        ...(style || {}),
        objectPosition: `${imagePosX}% ${imagePosY}%`,
    };

    return (
        <div className="mx-auto max-w-2xl lg:mx-0">
            <img
                className="media-soft-shadow rounded-[var(--shape-radius)] object-cover ring-neutral-400/10 transition duration-500 hover:scale-110"
                src={ImageUrl}
                alt={AltText}
                loading="lazy"
                style={imageStyle}
            />
        </div>
    );
}
